import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { formatTime12h, formatLongDate, formatDateMonthDay, tzAbbrev } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Home, Bell, Calendar, Star, Camera, ChevronRight, Loader2,
  MapPin, Clock, DollarSign, Shield, FileText, AlertTriangle,
  XCircle, ArrowRight, User, LogOut, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import ShiftConfirmationWidget from '../../components/talent/ShiftConfirmationWidget';
import EnablePushBanner from '../../components/EnablePushBanner';
import TalentMarketInfoBanner from '../../components/TalentMarketInfoBanner';  // Iter-411u
import AddToCalendarButton from '../../components/AddToCalendarButton';
import OnboardingChecklist from '../../components/OnboardingChecklist';
import ClockInReminder from '../../components/ClockInReminder';
import PendingRatingsWidget from '../../components/PendingRatingsWidget';
import MyDigitalBusinessCard from '../../components/MyDigitalBusinessCard';
import CardAnalytics from '../../components/CardAnalytics';
import CollapsibleSection from '../../components/CollapsibleSection';
import EventHighlightsWidget from '../../components/EventHighlightsWidget';
import WelcomeVideo from '../../components/WelcomeVideo';
import { HelpButton } from '../../components/HelpCenter';
import HelpMenu from '../../components/HelpMenu';
import CoCReacceptGate from '../../components/CoCReacceptGate';
import PostActivationKit from '../../components/PostActivationKit';
import QuickActionBar from '../../components/QuickActionBar';
// Iter-250 — Community adoption widgets
import CommunityHighlights from '../../components/CommunityHighlights';
import CommunityIntroModal, { useCommunityIntroFlag } from '../../components/CommunityIntroModal';
import { introShownThisSession, markIntroShownThisSession } from '../../utils/introSession';
import { useNoOtherDialogOpen } from '../../hooks/useNoOtherDialogOpen';
import SocialPerformanceCard from '../../components/SocialPerformanceCard';
import Lock24hBanner from '../../components/Lock24hBanner';
import PendingRecapBadge from '../../components/PendingRecapBadge';
import RejectionBanner from '../../components/RejectionBanner';  // Iter-220p — surface IDV/cert rejections
import ProfileBoostPromoModal from '../../components/ProfileBoostPromoModal';  // Iter-221 — $4.99 boost promotion (one-shot per 24h)
import ProfileBoostInlineBanner from '../../components/ProfileBoostInlineBanner';
import PaymentReadinessBanner from '../../components/PaymentReadinessBanner';  // Iter-315c — persistent Action Required
import RoleConfirmationModal from '../../components/RoleConfirmationModal';  // Iter-225p — confirm roles after StaffConnect import
import GPSConsentModal from '../../components/GPSConsentModal';  // Iter-263 — GPS consent back-fill
import MessagesIconButton from '../../components/MessagesIconButton';  // Iter-268 — Messages Center top-nav icon

// Compliance Status Indicator Component
const ComplianceStatusIndicator = ({ compliance, navigate }) => {
  if (!compliance) return null;

  const { is_fully_compliant, blockers, idv_status, contracts_signed, profile_complete } = compliance;

  // If fully compliant, hide the card entirely on Home — the user has
  // already acknowledged this state. The same status is now delivered
  // ONCE via the Messages Center (Iter-268).
  if (is_fully_compliant) {
    return null;
  }

  const getBlockerIcon = (type, status) => {
    if (type === 'idv') {
      if (status === 'pending') return <Clock className="w-4 h-4 text-amber-500" />;
      if (status === 'rejected') return <XCircle className="w-4 h-4 text-red-500" />;
      return <Shield className="w-4 h-4 text-amber-500" />;
    }
    if (type === 'contracts') return <FileText className="w-4 h-4 text-amber-500" />;
    return <User className="w-4 h-4 text-cyan-500" />;
  };

  const getStatusColor = (status) => {
    if (status === 'rejected') return 'border-red-200 bg-red-50';
    if (status === 'pending') return 'border-amber-200 bg-amber-50';
    return 'border-amber-200 bg-amber-50';
  };

  return (
    <Card className="border-amber-200 bg-amber-50" data-testid="compliance-status-incomplete">
      <CardHeader className="py-3 px-4 pb-0">
        <CardTitle className="text-sm font-medium text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Complete These Steps to Start Booking
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-3 space-y-2">
        {blockers.map((blocker, index) => (
          <div
            key={index}
            className={`flex items-center justify-between p-3 rounded-lg border ${getStatusColor(blocker.status)}`}
          >
            <div className="flex items-center gap-3">
              {getBlockerIcon(blocker.type, blocker.status)}
              <div>
                <p className="font-medium text-gray-800 text-sm">{blocker.message}</p>
                <p className="text-xs text-gray-500">{blocker.action}</p>
              </div>
            </div>
            {blocker.action_url && (
              <Button
                size="sm"
                variant={blocker.status === 'rejected' ? 'default' : 'ghost'}
                onClick={() => navigate(blocker.action_url)}
                className={blocker.status === 'rejected'
                  ? 'bg-rose-600 hover:bg-rose-700 text-white font-semibold shadow-sm'
                  : 'text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50'}
                data-testid={`blocker-action-${blocker.type}`}
              >
                {blocker.status === 'pending' ? 'View' : blocker.status === 'rejected' ? 'Re-upload now' : 'Fix'}
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        ))}

        {/* Progress indicator */}
        <div className="pt-2 mt-2 border-t border-amber-200">
          <div className="flex items-center justify-between text-xs text-amber-700 mb-1">
            <span>Onboarding Progress</span>
            <span>
              {(idv_status === 'approved' ? 1 : 0) + (contracts_signed ? 1 : 0) + (profile_complete ? 1 : 0)}/3 complete
            </span>
          </div>
          <Progress
            value={((idv_status === 'approved' ? 1 : 0) + (contracts_signed ? 1 : 0) + (profile_complete ? 1 : 0)) / 3 * 100}
            className="h-1.5"
          />
        </div>
      </CardContent>
    </Card>
  );
};

const TalentHome = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const handleLogout = async () => {
    try { await logout(); } catch (e) { /* ignore */ }
    navigate('/login');
  };
  const [loading, setLoading] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [recentCompletedBooking, setRecentCompletedBooking] = useState(null);

  useEffect(() => {
    fetchHomeData();
    fetchRecentCompletedBooking();
    // Listen for global profile updates (photo, bio, gig roles) so the
    // onboarding banner refreshes immediately instead of showing a stale state.
    const onUpdate = () => fetchHomeData();
    window.addEventListener('talent-profile-updated', onUpdate);
    return () => window.removeEventListener('talent-profile-updated', onUpdate);
  }, []);

  // Iter-263 — GPS consent back-fill.
  const [gpsConsentNeeded, setGpsConsentNeeded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/gps/consent/status');
        if (!cancelled && r.data?.needs_consent) setGpsConsentNeeded(true);
      } catch (_) { /* ignore — non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchHomeData = async () => {
    try {
      const res = await api.get('/home');
      setHomeData(res.data);
    } catch (e) {
      toast.error('Failed to load home data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentCompletedBooking = async () => {
    try {
      const res = await api.get('/talent/bookings?status=completed&limit=1');
      const bookings = res.data.bookings || res.data || [];
      if (bookings.length > 0) {
        const booking = bookings[0];
        const completedAt = new Date(booking.updated_at || booking.created_at);
        const hoursAgo = (Date.now() - completedAt.getTime()) / (1000 * 60 * 60);
        if (hoursAgo <= 24) {
          setRecentCompletedBooking({
            venue_name: booking.venue_name,
            brand_name: booking.client_company_name,
            location: booking.city,
            event_type: booking.role_name
          });
        }
      }
    } catch (e) {
      console.error('Failed to fetch recent booking:', e);
    }
  };

  // Iter-292g — Community intro modal.
  const { seen: introSeen } = useCommunityIntroFlag();
  const [showCommunityIntro, setShowCommunityIntro] = useState(false);
  const safeToOpenCommunity = useNoOtherDialogOpen(!introSeen && !showCommunityIntro, 'community-intro-modal');
  useEffect(() => {
    if (introSeen) {
      setShowCommunityIntro(false);
      return;
    }
    if (!safeToOpenCommunity) return; // wait for other modals to clear
    if (introShownThisSession()) return; // one friendly intro per session
    // Smart welcome order — show the ONE intro a talent needs most first.
    // • Profile still incomplete → they need to FINISH their profile, so the
    //   Getting-Started walkthrough (fires at ~500ms) should win; we hold the
    //   community intro back so it doesn't preempt it.
    // • Profile complete → nudge them to EXPLORE THE COMMUNITY first, so the
    //   community intro fires ahead of the lower-priority boost promo (1400ms).
    const profileComplete = !!(homeData?.compliance_status?.is_fully_compliant)
      && (homeData?.profile?.completion_pct || 0) >= 100;
    const delay = profileComplete ? 900 : 2500;
    const t = setTimeout(() => { markIntroShownThisSession(); setShowCommunityIntro(true); }, delay);
    return () => clearTimeout(t);
  }, [introSeen, safeToOpenCommunity, homeData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const { profile, upcoming_gigs, matching_gigs, reminders, notifications_count, compliance_status } = homeData || {};

  // Show onboarding helpers (checklist + welcome video) ONLY for talent who
  // haven't finished onboarding yet. Once fully compliant / profile complete
  // they disappear from Home so it stays action-focused. OnboardingChecklist
  // also self-hides on completion; this gates the WelcomeVideo the same way.
  const isNewTalent = !(compliance_status?.is_fully_compliant) || (profile?.completion_pct || 0) < 100;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-home">
      <CommunityIntroModal open={showCommunityIntro} onClose={() => setShowCommunityIntro(false)} />
      <CoCReacceptGate />
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button onClick={() => navigate('/talent/home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity shrink-0" data-testid="home-link">
              <Home className="w-5 h-5 text-cyan-500" />
              <span className="text-sm font-bold text-cyan-600 hidden sm:inline">iEvntur</span>
            </button>
            <h1 className="text-base sm:text-lg font-semibold text-gray-900 truncate" data-testid="talent-greeting">{(() => { const h = new Date().getHours(); const name = homeData?.profile?.nickname || homeData?.profile?.first_name || ''; return h < 12 ? `Good morning${name ? ', ' + name : ''}` : h < 17 ? `Good afternoon${name ? ', ' + name : ''}` : `Good evening${name ? ', ' + name : ''}`; })()}</h1>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <HelpMenu />
            <Button variant="ghost" size="sm" onClick={() => navigate('/talent/notifications')} className="relative px-2">
              <Bell className="w-5 h-5" />
              {notifications_count > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {notifications_count}
                </span>
              )}
            </Button>
            {/* Iter-268 — Messages Center icon */}
            <MessagesIconButton targetPath="/talent/messages-center" unreadSource="/messages/talent/comms-unread" />
            <Button variant="ghost" size="sm" onClick={() => navigate('/talent/settings')} className="px-2" title="Settings & Appearance" data-testid="talent-home-settings-btn">
              <Settings className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/talent/dashboard')} className="px-2" title="Profile" data-testid="talent-home-profile-btn">
              <User className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="px-2 text-gray-600 hover:text-red-700" data-testid="talent-home-logout-btn" title="Logout">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Iter-192.2 — Lock in next 24h sticky banner (urgent, time-critical). */}
      <Lock24hBanner />

      {/* Iter-221 — Profile Boost promo modal (24h cooldown, self-suppresses if boost active). */}
      <ProfileBoostPromoModal />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4" data-testid="talent-home-content">
        {/* ─────────────────────────────────────────────────────────────
            1️⃣ URGENT / TIME-SENSITIVE ALERTS — always pinned to the top so
               shift confirmations, payment blockers, and rejections are
               never buried below promotional content.
           ───────────────────────────────────────────────────────────── */}
        <ClockInReminder />
        <RejectionBanner />
        <PendingRecapBadge />
        <RoleConfirmationModal />
        <PaymentReadinessBanner />
        <ComplianceStatusIndicator compliance={compliance_status} navigate={navigate} />
        <ShiftConfirmationWidget />
        <EnablePushBanner />
        <TalentMarketInfoBanner />

        {/* ── MY DIGITAL BUSINESS CARD — promoted to the very TOP of the
               dashboard (right under urgent alerts, ABOVE the quick-action
               bar) so talent see their self-marketing tool immediately and
               prominently on login. Card preview, QR code, sharing options
               and analytics stay together and accessible. Booking-link
               management lives in Profile Settings → Booking link & custom
               URL. ── */}
        <MyDigitalBusinessCard />

        {/* ── Digital Business Card analytics — compact summary that expands
               into the full private reporting dashboard. Each talent only
               ever sees their own numbers. ── */}
        <CardAnalytics />

        {/* ── QUICK ACTIONS — Chat with Evie, Settlements, My Bookings. ── */}
        <QuickActionBar role="talent" />

        {/* ── 2️⃣ $4.99 PROFILE BOOST (actively promoted) ── */}
        <ProfileBoostInlineBanner source="talent_home" />

        {/* ── 3️⃣ COMMUNITY FEED / SHARE A MOMENT ── */}
        <CommunityHighlights />

        {/* ── New-talent onboarding helpers — auto-hide once onboarding is
               complete so Home stays uncluttered for established talent. ── */}
        <OnboardingChecklist />
        {isNewTalent && (
          <WelcomeVideo
            title="Getting Started"
            subtitle="Learn how to succeed on iEvntür"
            storageKey="ievntur_talent_welcome_video_dismissed"
            settingsKey="talent_welcome_video_url"
            cardDescription="Complete your profile, find gigs & get booked"
            accentColor="purple"
            showOnFirstVisit={true}
          />
        )}

        {/* ─────────────────────────────────────────────────────────────
            6️⃣ CURRENT BOOKINGS, INVITATIONS & IMMEDIATE OPPORTUNITIES
           ───────────────────────────────────────────────────────────── */}
        <EventHighlightsWidget />

        {upcoming_gigs?.length > 0 && (
          <Card
            className="border-cyan-300 bg-cyan-50 shadow-sm"
            data-testid="upcoming-gigs-section"
          >
            <CardHeader className="py-3 px-4">
              <CardTitle className="text-base font-semibold text-cyan-900 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Upcoming Gigs This Week
                <Badge className="ml-2 bg-cyan-500 text-white" data-testid="upcoming-gigs-count">
                  {upcoming_gigs.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-2">
                {upcoming_gigs.map(gig => (
                  <div
                    key={gig.booking_id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-white rounded-lg border border-cyan-100 hover:border-cyan-300 transition-colors"
                    data-testid={`upcoming-gig-${gig.booking_id}`}
                  >
                    <div
                      className="min-w-0 flex-1 cursor-pointer"
                      onClick={() => navigate('/talent/bookings')}
                    >
                      <p className="font-semibold text-gray-900 truncate">{gig.venue_name}</p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {gig.company_name} • {formatLongDate(gig.shift_date)} at {formatTime12h(gig.start_time)} {tzAbbrev(gig.timezone, gig.state, gig.shift_date)}
                      </p>
                      {(gig.city || gig.state) && (
                        <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3" />
                          {[gig.city, gig.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
                      <Badge variant="outline">{gig.role_name}</Badge>
                      <AddToCalendarButton
                        bookingId={gig.booking_id}
                        label="Calendar"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Post-event kit shows immediately after a recent booking completion. */}
        {recentCompletedBooking && (
          <PostActivationKit
            recentBooking={recentCompletedBooking}
            onUploadPhotos={() => navigate('/talent/bookings')}
            compact={false}
          />
        )}

        {matching_gigs?.length > 0 && (
          <Card data-testid="matching-gigs-section">
            <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-medium text-gray-700">Available Gigs You Match</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/talent/shifts')}>
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <div className="space-y-2">
                {matching_gigs.slice(0, 3).map(gig => (
                  <div
                    key={gig.gig_id}
                    className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:border-cyan-200 hover:shadow-sm transition cursor-pointer"
                    onClick={(e) => { if (e.target.closest('button,a,[role="button"]')) return; navigate(`/talent/gigs/${gig.gig_id}`); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/talent/gigs/${gig.gig_id}`); } }}
                    data-testid={`matching-gig-${gig.gig_id}`}
                  >
                    <div>
                      <p className="font-medium text-gray-900">{gig.venue_name}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {gig.city}, {gig.state}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateMonthDay(gig.shift_date)}
                        </span>
                        {gig.pay_rate && (
                          <span className="flex items-center gap-1 text-green-600 font-medium">
                            <DollarSign className="w-3 h-3" />
                            ${gig.pay_rate}/hr
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge className="bg-cyan-100 text-cyan-700">{gig.role_name}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pending ratings on completed gigs */}
        <PendingRatingsWidget userRole="talent" />

        {/* Notifications / reminders panel — collapsible to keep Home tidy */}
        {(reminders?.pending_quizzes?.length > 0 || reminders?.pending_media_uploads?.length > 0 || reminders?.pending_ratings_count > 0) && (
          <CollapsibleSection
            title="Notifications"
            subtitle="Quizzes, media uploads and ratings to complete"
            badge={(reminders?.pending_quizzes?.length || 0) + (reminders?.pending_media_uploads?.length || 0) + (reminders?.pending_ratings_count > 0 ? 1 : 0)}
            className="border-cyan-200 bg-cyan-50/50"
            testId="reminders-section"
          >
            <div className="space-y-2">
              {reminders?.pending_quizzes?.map(quiz => (
                <div key={quiz.id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Quiz: {quiz.title}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              ))}
              {reminders?.pending_media_uploads?.map(gig => (
                <div key={gig.gig_id} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">Upload media: {gig.venue_name}</span>
                  <Camera className="w-4 h-4 text-gray-400" />
                </div>
              ))}
              {reminders?.pending_ratings_count > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{reminders.pending_ratings_count} gig(s) to rate</span>
                  <Star className="w-4 h-4 text-gray-400" />
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* ── 7️⃣ LIMITED SOCIAL SHARING (kept near the bottom) ── */}
        <SocialPerformanceCard />
      </div>

      {/* Help Center Button */}
      <HelpButton userType="talent" />

      {/* Iter-263 — GPS Attendance Verification consent back-fill modal. */}
      <GPSConsentModal
        open={gpsConsentNeeded}
        consentType="login_modal"
        blocking
        onAccept={() => setGpsConsentNeeded(false)}
      />
    </div>
  );
};

export default TalentHome;
